import { useIncludeHidden } from '@/lib/includeHidden';
import { SHOW_FARM_TAG } from '@/lib/farmTag.core';
import { useUiMode } from '@/hooks/useUiMode';
import { dashboardSectionInMode } from '@/lib/uiMode.core';
import { track } from '@/lib/analytics';
import { TodayPanel } from '@/components/dashboard/TodayPanel';
import { FoldSection } from '@/components/engagement/FoldSection';
import React, { useState, useMemo, useEffect } from 'react';
import { useSyncedPreference } from '@/hooks/useSyncedPreference';
import { AssetFilterControls, SymbolLabel } from '@/components/AssetType';
import { type AssetFilter, NO_ASSET_FILTER, assetFilterActive, shownFilter } from '@/lib/instruments';
import { clampPage, pageSlice } from '@/lib/pageClamp';
import { useDashboardMetrics, useTopTraders, useRecentTrades, useTopTrades, useRecentlyOpenedTrades, usePrefetchTopTradePeriods, usePrefetchTopTraderPeriods } from '@/hooks/useDashboardData';
import { openPnlOf } from '@/lib/openPnl';
import { venueScope } from '@/lib/venueScope';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { QueryDebugPanel } from '@/components/QueryDebugPanel';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateTimeCell } from '@/components/ui/date-time-cell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { intlLocale, fmtFixed, fmtPct, fmtUsd, withSign } from '@/lib/format';
import { LiveIndicator } from "@/components/ui/live-indicator";
import { CaretRight, CaretLeft, Info, ArrowClockwise, WarningCircle, Scales } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Link, useNavigate } from 'react-router-dom';
import { traderPath, traderLinkState } from '@/lib/traderSlug';
import { useSessions } from '@/hooks/useSessions';
import { useFavorites } from '@/hooks/useTraderData';
import { usePortfolioPositions } from '@/hooks/usePortfolio';
import { PortfolioSection } from '@/components/dashboard/PortfolioSection';
import { MarketBand } from '@/components/dashboard/MarketBand';
import { RegimeWidget } from '@/components/dashboard/RegimeWidget';
import { useStatsWebSocket } from '@/hooks/useWebSocket';
import { useLivePricesSubscription } from '@/hooks/useLivePrices';
import Avatar from 'boring-avatars';
import { tradeTableState } from '@/lib/tradeTableState';
import { TRADER_COLUMNS, type TraderColumn } from '@/lib/traderColumns';
import { TraderMetricFilters, colFilterCount, passesColFilters } from '@/components/TraderFilters';
import { TraderColumnPicker } from '@/components/TraderColumnPicker';
import { useCompareNames, addToCompare, removeFromCompare, COMPARE_MAX } from '@/lib/compareTraders';
import { VsLauncher } from '@/components/compare/CompareDialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { HeardFromCard } from '@/components/dashboard/HeardFromCard';
import { emitOnboarding } from '@/lib/onboarding';
const TradeDetailsModal = React.lazy(() => import('@/components/ui/trade-details-modal'));

// PRICE_SYMBOLS, formatPrice and symbolLabel moved with the price rail into
// LivePricesTable, which now owns every price this page renders.


/****************
 * The sort list is GENERATED from the columns (lib/traderColumns.tsx), never written beside them.
 *
 * It used to be a hand-maintained list of six <SelectItem>s next to a list of
 * eighteen columns, so adding a column added a header you could click and left
 * the dropdown one entry short. Sortino, Profit Factor, Avg Leverage, Wallet
 * Size, Total Volume, Wins, Losses, Avg Win, Avg Loss, Unrealized PnL and
 * Active Positions were all sortable by clicking and absent from the list of
 * ways to sort. Two lists, one truth, and only one of them was ever updated.
 *
 * Balanced Score survives as the one preset that is not a column: it is a blend
 * of three, so there is no header to click for it.
 ****************/
const BALANCED_SORT = 'balanced';

// 12 columns by default with rank and trader (Guillaume's /preview review #24, 10-24): Profit factor
// and Avg leverage added. A saved choice still equal to the old seven-column default is read as the new one.
const DEFAULT_TRADER_COLS = ['winRate', 'trades', 'avgDuration', 'maxDrawdown', 'avgDrawdown', 'roi', 'totalPnl', 'profitFactor', 'avgLeverage'];
const OLD_DEFAULT_TRADER_COLS = ['winRate', 'trades', 'avgDuration', 'maxDrawdown', 'avgDrawdown', 'roi', 'totalPnl'];
const TRADER_COLS_STORAGE = 'copyfi.topTraders.columns';

// Columns that describe now, not a period: never "-" for a quiet period.
const NOW_COLUMNS = new Set(['walletSize', 'activePositions', 'biggestPosition', 'netPosition', 'unrealizedPnl']);

export default function Dashboard() {
    const { t } = useTranslation('dashboard');
    const { t: tc } = useTranslation('common');
    const navigate = useNavigate();
    // CopyFi Lite shows Today and your portfolio only; Pro adds the market, the full
    // Top Traders table and every trade (Guillaume 2026-10-22, lib/uiMode.core.ts).
    const { mode: uiMode } = useUiMode();
    /* Remembered across navigation, like every other control on this page.
       Guillaume, 2026-09-23: "when a user chose Month and navigate, when he
       comes back on this page Month is still selected." useSyncedPreference,
       so the choice travels to the phone too -- the same hook the column
       layout and the watchlist already use. */
    const [period, setPeriod] = useSyncedPreference<string>("copyfi.dashboard.period", "all");
    const [traderPage, setTraderPage] = useState(0);
    const [selectedTrade, setSelectedTrade] = useState<any>(null);
    // 'totalPnl' -- a COLUMN key, not the old 'pnl' preset. The presets and the
    // columns were two vocabularies for the same six sorts, and the preset half
    // could not express the other twelve columns. One vocabulary now.
    const [sortBy, setSortBy] = useSyncedPreference<string>('copyfi.dashboard.sortBy', 'totalPnl');
    // Direction is explicit rather than implied by lowerBetter. Clicking a
    // header a second time reverses it: "who has the worst drawdown" is as real
    // a question as "who has the best", and there was no way to ask it.
    const [sortDesc, setSortDesc] = useSyncedPreference<boolean>('copyfi.dashboard.sortDesc', true);
    // The below-floor opt-in. Off on every load -- deliberately not remembered,
    // because a switch that reveals traders we exclude for losing more than
    // $10,000 should be a decision the user makes today, not one they made once
    // and forgot. 2026-09-03.
    // Shared with the Traders page, off on every load (lib/includeHidden).
    const [includeBelowFloor, setIncludeBelowFloor] = useIncludeHidden();
    /*
     * Asset type and broker availability (2026-10-04), filtered on the server:
     * the leaderboard ranks traders on the matching trades only, and the
     * trades section lists only matching trades. One choice for each panel.
     */
    const [savedLeaderAssets, setLeaderAssets] = useSyncedPreference<AssetFilter>('copyfi.dashboard.leaderAssets', NO_ASSET_FILTER);
    const [savedTradeAssets, setTradeAssets] = useSyncedPreference<AssetFilter>('copyfi.dashboard.tradeAssets', NO_ASSET_FILTER);
    const leaderAssets = useMemo(() => shownFilter(savedLeaderAssets), [savedLeaderAssets]);
    const tradeAssets = useMemo(() => shownFilter(savedTradeAssets), [savedTradeAssets]);
    // Guillaume, 2026-09-22: a Favourites-only filter on this table.
    const [favouritesOnly, setFavouritesOnly] = useSyncedPreference<boolean>('copyfi.dashboard.favouritesOnly', false);
    const { favorites } = useFavorites();
    const tradersPerPage = 10;

    // Customizable Top Traders columns + per-metric filters. Column choice AND
    // ORDER are remembered across sessions: the array is the display order, not
    // a set. It used to be filtered through the canonical order on render, so
    // the picker chose which columns appeared and nothing chose where.
    // 2026-08 (Nick), ordering 2026-09-03.
    const [visibleCols, setVisibleColsSynced] = useSyncedPreference<string[]>(
        TRADER_COLS_STORAGE,
        DEFAULT_TRADER_COLS,
    );
    // The column picker (components/TraderColumnPicker) hands over the whole
    // new order; the hook writes storage itself.
    const setVisibleCols = (next: string[]) => setVisibleColsSynced(next);
    const [colFilters, setColFilters] = useState<Record<string, { min?: string; max?: string }>>({});

    const clearFilters = () => { setColFilters({}); setLeaderAssets(NO_ASSET_FILTER); setTraderPage(0); };
    // The min/max filters and the saved combinations live in
    // components/TraderFilters, shared with the asset page's trader table.
    const activeFilterCount = colFilterCount(colFilters)
        + (leaderAssets.assetClass !== 'all' ? 1 : 0) + (leaderAssets.broker !== 'any' ? 1 : 0) + (leaderAssets.asset ? 1 : 0);
    // traders_filter_applied (2026-10-22): once the filters settle, not per keystroke.
    useEffect(() => {
        if (activeFilterCount === 0) return;
        const tm = window.setTimeout(() => track('traders_filter_applied', undefined, undefined, { count: activeFilterCount }), 1200);
        return () => window.clearTimeout(tm);
    }, [activeFilterCount, colFilters]);
    // Columns render in the USER'S order. Mapping over visibleCols rather than
    // filtering TRADER_COLUMNS is the whole of the ordering feature; the filter
    // form silently imposed the declaration order no matter what was stored.
    // The Boolean filter drops a stored key that no longer names a column, so a
    // removed or renamed column cannot crash the table for whoever had it saved.
    const shownColumns = (visibleCols.join() === OLD_DEFAULT_TRADER_COLS.join() ? DEFAULT_TRADER_COLS : visibleCols)
        .map(k => TRADER_COLUMNS.find(c => c.key === k))
        .filter(Boolean) as TraderColumn[];

    /*
     * The venue is passed explicitly, because an absent one now means BINANCE on the server
     * (venue_policy.go: a client predating venues must not widen by accident). This page has
     * no venue chooser, so it asks for what any viewer may see -- which is every venue since
     * MEXC went public. Leaving it off would have quietly narrowed the dashboard's headline
     * numbers to Binance the moment the summary learned about venues.
     */
    const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics(period, venueScope(false));
    // The whole roster (limit 0 = none). 500 cut MEXC from 982 traders to 500
    // on 2026-10-07 while the Traders page listed them all: Guillaume, "these
    // row limit should not exist anywhere". The table pages what it holds.
    const { data: topTraders, isError: tradersError, refetch: refetchTraders, isPlaceholderData: tradersArePrevious } =
        useTopTraders(0, period, includeBelowFloor, undefined, leaderAssets);
    // Trader vs Trader (2026-09-26): who is in the comparison, for the row toggles.
    const compareNames = useCompareNames();
    // The leaderboard's other windows, same rule as Best Trades below.
    usePrefetchTopTraderPeriods(0, period, ['all', '1m', '1w'],
        includeBelowFloor, Boolean(topTraders));
    const [tradeView, setTradeView] = useSyncedPreference<'recent' | 'best' | 'opened'>('copyfi.dashboard.tradeView', 'recent');
    /** Best Trades' own window, ranked server-side over it. */
    const [tradePeriod, setTradePeriod] = useSyncedPreference<'24h' | '1w' | '1m'>('copyfi.dashboard.tradePeriod', '1w');
    /****************
     * EVERY TRADE, A PAGE AT A TIME. Guillaume, 2026-10-07: "Same logic must
     * apply to the top trades below".
     *
     * The three views read 100 trades and showed 10, and "Show all" showed the
     * 100; the 122,962 others could not be reached. Each page is now asked of
     * the server, which sorts the whole feed and says how many it holds, so
     * the pager runs to the last trade. Back to the first page whenever the
     * view, the window or the asset filter changes.
     ****************/
    const TRADES_PER_PAGE = 10;
    /* SHOW MORE, NOT PAGES. Guillaume, 2026-10-09: "I don't like the Previous and
       Next button, I preferred the old 'Show More' option with new rows being
       added to the table." Still one server page of ten at a time, so every
       trade stays reachable (the reason for the pager, 10-07); the pages
       already read stay on screen above the new one. */
    const [tradePage, setTradePage] = useState(0);
    const [earlierTradeRows, setEarlierTradeRows] = useState<any[]>([]);
    const tradesShown = React.useRef<any[]>([]);
    useEffect(() => { setTradePage(0); setEarlierTradeRows([]); }, [tradeView, tradePeriod, tradeAssets]);
    const tradeOffset = tradePage * TRADES_PER_PAGE;
    const { data: recentTrades, total: recentTotal, isError: recentTradesError, refetch: refetchRecentTrades, isPlaceholderData: recentArePrevious } =
        useRecentTrades(TRADES_PER_PAGE, 'all', undefined, tradeAssets, tradeView === 'recent' ? tradeOffset : 0);
    // Third view, live (Guillaume, 2026-09-22). Polls only while it is on screen.
    const { data: openedTrades, total: openedTotal, isPlaceholderData: openedArePrevious } = useRecentlyOpenedTrades(TRADES_PER_PAGE, tradeView === 'opened', undefined,
        Boolean(recentTrades), tradeAssets, tradeView === 'opened' ? tradeOffset : 0);
    /*
     * isError and isPlaceholderData were discarded here, so a window that
     * had not arrived yet -- or had failed -- rendered the PREVIOUS
     * window's rows under the new label, which is what "does not always
     * work" looks like from the outside. GB-3, 2026-09-20.
     */
    const {
        data: topTrades,
        total: topTotal,
        isError: topTradesError,
        isPlaceholderData: topTradesArePreviousWindow,
    } = useTopTrades(TRADES_PER_PAGE, tradePeriod, undefined, tradeAssets, tradeView === 'best' ? tradeOffset : 0);
    /* THE NEXT TEN, READ BEFORE THE CLICK. Guillaume, 2026-10-10: "Show 10 more"
       was slow (each page is its own server read). The view on screen asks for
       its next page now, so the click finds it in hand. The other views ask for
       their first page, which is the same read they already made. */
    const nextTradeOffset = tradeOffset + TRADES_PER_PAGE;
    useRecentTrades(TRADES_PER_PAGE, 'all', undefined, tradeAssets, tradeView === 'recent' ? nextTradeOffset : 0);
    useRecentlyOpenedTrades(TRADES_PER_PAGE, tradeView === 'opened', undefined, Boolean(recentTrades), tradeAssets, tradeView === 'opened' ? nextTradeOffset : 0);
    useTopTrades(TRADES_PER_PAGE, tradePeriod, undefined, tradeAssets, tradeView === 'best' ? nextTradeOffset : 0);
    /****************
     * AND THE WINDOWS NOBODY HAS CLICKED YET. GB-3, 2026-09-22.
     *
     * Guillaume: "every time we need to swap for displaying A to display B,
     * the first swap is slow, than swapping between them is okay."
     *
     * Fetched once the window on screen has arrived, and on an idle browser,
     * so the view somebody IS looking at never queues behind one they might
     * not open. hooks/usePrefetchChoices.ts.
     ****************/
    usePrefetchTopTradePeriods(TRADES_PER_PAGE, tradePeriod, ['24h', '1w', '1m'],
        Boolean(topTrades) && !topTradesArePreviousWindow);
    /*
     * ONE answer, computed once.
     *
     * This was worked out twice -- once to decide whether to draw rows, once
     * to decide what the message row says -- with inputs that happened to
     * match. That is the shape this repository has already paid for: the
     * trader pseudonym existed in two packages with a comment asking humans to
     * keep them in step, and they drifted. Two call sites cannot disagree
     * about which window is on screen if there is only one place that decides.
     */
    const bestTradesWindowLabel = tradePeriod;
    const bestTradesState = tradeTableState({
        rowCount: (topTrades || []).length,
        isError: topTradesError,
        isPlaceholder: topTradesArePreviousWindow,
    });
    /* Best Trades is a different query, not a filter of the one above. The
       pager below runs over the whole feed of the view on screen. */
    const tradesTotal = (tradeView === 'best' ? topTotal : tradeView === 'opened' ? openedTotal : recentTotal) ?? 0;
    const totalTradePages = Math.max(1, Math.ceil(tradesTotal / TRADES_PER_PAGE));

    /****************
     * THE SCROLL JUMP. GB-3, 2026-09-23.
     *
     * Guillaume: "it also do a scroll up which is bothering".
     *
     * Nothing scrolls the page. The table does: swapping Today for Month drops
     * every row (the rows in hand belong to the old window and showing them
     * under the new label is the defect this state machine exists to prevent)
     * and renders ONE row saying "Loading month…". Ten rows become one, the
     * document shortens by several hundred pixels, and the browser has no
     * choice but to clamp the scroll position. When the new rows land the page
     * grows back and you are somewhere above where you were standing.
     *
     * So the table keeps its HEIGHT while it has no rows: the same number of
     * empty rows it was showing a moment ago. Same components, same geometry,
     * so the reservation is exact rather than a guessed pixel height -- and it
     * needs no measurement, which would be a frame late by construction.
     *
     * 2026-09-25: and for the three views, not only Best Trades' windows.
     * Recent -> Recently Opened collapsed the table the same way while the
     * new view had not arrived. The count is whatever the table last showed,
     * set where the rows are sliced below.
     ****************/
    const tradesRowsHeld = React.useRef(0);

  /*
   * THE ARROWS WALK THE TABLE YOU CLICKED. 2026-09-22.
   *
   * Guillaume: "the Previous Trade and Next Trade button should allow you to
   * navigate the table where the trade you clicked on was in ... and not bring
   * me somewhere else." Every page walked one hard-coded list, so opening a
   * trade from Best Trades, Recently Opened or the closed table stepped
   * through a different one. The table that opened the modal hands its own
   * rows over here.
   */
    const [navTrades, setNavTrades] = useState<any[]>([]);
    const trades = navTrades.length > 0 ? navTrades : (recentTrades || []);
    const currentTradeIdx = selectedTrade ? trades.findIndex((t: any) => t.id === selectedTrade.id) : -1;
    const handlePrevTrade = currentTradeIdx > 0 ? () => {
        const prev = trades[currentTradeIdx - 1];
        setSelectedTrade({ ...prev, id: prev.id, tradeId: prev.id, asset: prev.symbol || 'BTC', direction: prev.direction, entry: `$${prev.avg_entry_price}`, exit: `$${prev.avg_exit_price}`, pnl: `${withSign(prev.pnl_usd >= 0, fmtUsd(prev.pnl_usd, 3, { minDigits: 0 }))}`, entryTime: prev.entry_time, exitTime: prev.exit_time });
    } : undefined;
    const handleNextTrade = currentTradeIdx >= 0 && currentTradeIdx < trades.length - 1 ? () => {
        const next = trades[currentTradeIdx + 1];
        setSelectedTrade({ ...next, id: next.id, tradeId: next.id, asset: next.symbol || 'BTC', direction: next.direction, entry: `$${next.avg_entry_price}`, exit: `$${next.avg_exit_price}`, pnl: `${withSign(next.pnl_usd >= 0, fmtUsd(next.pnl_usd, 3, { minDigits: 0 }))}`, entryTime: next.entry_time, exitTime: next.exit_time });
    } : undefined;
    // isLoading and isError were being discarded, so a query that was still in
    // flight — or one that had failed outright — rendered "+$0.00" and "0",
    // which reads as "you have no sessions". Same class of lie as telling a
    // user their own rules filtered an order when our code broke.
    const { data: sessions, isLoading: sessionsLoading, isError: sessionsError, refetch: refetchSessions } = useSessions(false);

    // Captured, not discarded: the LIVE badges need it. Subscribing inside the
    // badge would open one socket per badge — there is no shared provider here.
    const { isConnected: wsConnected } = useStatsWebSocket({ enabled: true });
    const livePrices = useLivePricesSubscription();
    const livePriceMap = useMemo(() => {
        const out: Record<string, number> = {};
        for (const [symbol, tick] of Object.entries(livePrices || {})) {
            const raw = (tick as any)?.p;
            const value = typeof raw === 'string' ? parseFloat(raw) : raw;
            if (typeof value === 'number' && Number.isFinite(value) && value > 0) out[symbol] = value;
        }
        return out;
    }, [livePrices]);

    const allSessions = sessions || [];
    const activeSessions = allSessions.filter((s: any) => s.status === 'active' || s.status === 'running');

    // Personal stats
    // Realised + unrealised, matching the session detail page. Summing
    // realised alone made "Your PnL" ignore every open position a user held.
    // 2026-08-31.
    // Every per-session figure moved INTO PortfolioSection, because the
    // paper/live scope decides which sessions count toward each of them.
    // Computing them here would mean computing each one twice, once per
    // scope, and the two copies drifting the first time either changed.

    // The user's own book, across every session. One request, not one per
    // session -- see usePortfolio.
    const { data: myPositions, isLoading: positionsLoading } = usePortfolioPositions();

    // Trader pagination
    const sortedTraders = useMemo(() => {
        if (!topTraders) return [];
        // Filter on any metric's min/max before sorting.
        const sorted = topTraders.filter((t: any) =>
            (!favouritesOnly || favorites.has(t.trader_id || t.trader_uid)) && passesColFilters(t, colFilters)
        );

        // Every column sorts, in either direction, from its own definition.
        const col = TRADER_COLUMNS.find(c => c.key === sortBy);
        if (col) {
            return [...sorted].sort((a: any, b: any) => {
                const av = col.get(a);
                const bv = col.get(b);
                // Missing values sink to the bottom whichever way the column is
                // sorted. They are not zero, and ranking them as zero would put
                // a trader we know nothing about at the top of "lowest drawdown".
                const aMissing = av === null || av === undefined || Number.isNaN(av);
                const bMissing = bv === null || bv === undefined || Number.isNaN(bv);
                if (aMissing || bMissing) return aMissing === bMissing ? 0 : aMissing ? 1 : -1;
                return sortDesc ? (bv as number) - (av as number) : (av as number) - (bv as number);
            });
        }

        // The one sort that is not a column, because it is a blend of three.
        if (sortBy === BALANCED_SORT) {
            return [...sorted].sort((a: any, b: any) => {
                const scoreA = (a.win_rate || 0) * 0.4 + Math.min((a.total_trades || 0) / 100, 1) * 30 + Math.min((a.total_pnl_usd || 0) / 10000, 1) * 30;
                const scoreB = (b.win_rate || 0) * 0.4 + Math.min((b.total_trades || 0) / 100, 1) * 30 + Math.min((b.total_pnl_usd || 0) / 10000, 1) * 30;
                return sortDesc ? scoreB - scoreA : scoreA - scoreB;
            });
        }
        return sorted;
    }, [topTraders, sortBy, sortDesc, colFilters, favouritesOnly, favorites]);

    // Clicking a header sorts by it; clicking the SAME header again reverses.
    // A column's own sense of good picks the first direction, so one click on
    // Worst Trade shows the worst and one click on ROI shows the best.
    const sortByColumn = (col: TraderColumn) => {
        track('traders_sorted', col.key, undefined, { desc: sortBy === col.key ? !sortDesc : !col.lowerBetter });
        if (sortBy === col.key) {
            setSortDesc(d => !d);
        } else {
            setSortBy(col.key);
            setSortDesc(!col.lowerBetter);
        }
        emitOnboarding('dash_sort', { at: Date.now() });
        setTraderPage(0);
    };
    const totalTraderPages = Math.ceil((sortedTraders?.length || 0) / tradersPerPage);

    /****************
     * A PAGE INDEX MAY NEVER POINT PAST THE END OF THE LIST. 2026-09-23.
     *
     * Guillaume: "the 'Include Heavy Loser' don't seem to work... I filter
     * trader by PnL to keep only traders who lost more than 1k it doesn't show
     * anybody. Must be a row cap issue again."
     *
     * It was not a row cap, and the switch works -- the API returns 217
     * traders with it and 187 without, 28 of them correctly below the floor.
     * It was this line. Ten traders to a page, so 217 of them is 22 pages;
     * filter to "lost more than $1,000" and 87 survive, which is 9 pages. The
     * page INDEX stayed where it was, slice() past the end returns nothing,
     * and the table goes blank. Reproduced against production: page 10, 13 and
     * 22 all return 0 rows off a list that has 87 in it.
     *
     * Clicking a column header already reset the page (setTraderPage(0) just
     * above). Every OTHER way the list can shrink -- the column filters, the
     * favourites toggle, the below-floor switch, the period -- did not, which
     * is why sorting felt fine and filtering looked broken.
     *
     * TWO fixes, because they cover different failures.
     *
     * The CLAMP is the one that cannot be forgotten. It makes the invalid
     * state unrepresentable rather than listing the things that must remember
     * to reset, and it also covers the case no reset can: the list is
     * refetched every poll, so it can shrink while the user is touching
     * nothing at all.
     *
     * The RESET is the correct behaviour on a deliberate change. Landing on
     * page 9 of a filter you just typed is not useful; you want the top of the
     * new list. The clamp alone would leave you mid-way through it.
     ****************/
    const safeTraderPage = clampPage(traderPage, sortedTraders?.length || 0, tradersPerPage);
    useEffect(() => {
        // Changing what the list CONTAINS means starting at the top of it.
        setTraderPage(0);
    }, [colFilters, favouritesOnly, includeBelowFloor, period]);

    const paginatedTraders = pageSlice(sortedTraders || [], traderPage, tradersPerPage);

    if (metricsLoading) {
        return (
            <div className="p-6 lg:p-10 space-y-6">
                <Skeleton className="h-8 w-48" />
                <div className="grid grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
                </div>
                <Skeleton className="h-96 w-full rounded-lg" />
            </div>
        );
    }

    const totalVolume = (metrics?.longOpenVolume || 0) + (metrics?.shortOpenVolume || 0);
    const activeTraders = metrics?.activeTraderCount || 0;

    return (
        <div className="p-6 lg:p-10">
            {/* No page title: the performance card is "Your Performance Dashboard" (Guillaume 11-04). */}

            {/* No header row: the buttons and overview line are gone and LIVE sits beside "Your performance" (review #78, 10-26). */}

            {/* New accounts, once: where they heard of CopyFi (2026-10-21). */}
            <HeardFromCard />

            {/* One click, and the page says whether a query never fired, is still
                in flight, or failed — the three things that all rendered $0.00
                and cost three rounds of guessing. app.copyfi.ai/?debug=1 */}
            {new URLSearchParams(window.location.search).get('debug') === '1' && <QueryDebugPanel />}

            {/* What happened, where you are, what's next (engagement redesign,
                2026-10-21). Everything below opens on demand. */}
            {/* Next step + lesson go inside the performance card (#78); alone only while that card is hidden. */}
            {/* Guillaume 10-31: an empty dashboard rather than none (review #41 of 10-24 hid it). Today is in its footer. */}

            {/* Your performance */}
            {sessionsError && (
                <div className="mb-3 px-4 py-3 rounded-xl border border-danger/40 bg-danger/5 text-sm flex items-center justify-between gap-4">
                    <div>
                        <p className="font-medium">{t('sessionsError.title')}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {t('sessionsError.body')}
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => refetchSessions()}>{t('sessionsError.retry')}</Button>
                </div>
            )}
            {/* Lite: no portfolio or performance while there is nothing in it yet (review #41, 10-24). */}
            {<div data-tour="dash-kpis">
            <FoldSection id="dash-portfolio" title={t('section.portfolio')} defaultOpen>
                <PortfolioSection
                    live={<LiveIndicator connected={wsConnected} />}
                    footer={<TodayPanel inCard />}
                    sessions={allSessions}
                    positions={myPositions}
                    positionsLoading={positionsLoading}
                    sessionsLoading={sessionsLoading}
                    sessionsError={sessionsError}
                />
            </FoldSection>
            </div>}

            {/* Flattened here, not inside the table: the socket's payload
                shape is this page's business, and the table only needs a
                symbol-to-price map. A string price is normal on this feed. */}
            {/* The regime tensor's live reading; renders nothing for an
                account the widget is not open to (RegimeWidget.tsx). */}
            {dashboardSectionInMode("dash-market", uiMode) && <div data-tour="dash-prices">
            {/* Pro opens Market, Top Traders and Trades by default; Lite keeps them closed
                (Guillaume's /preview review #21 and #41, 10-24). */}
            <FoldSection id="dash-market" title={t('section.market')} defaultOpen={uiMode === "pro"}>
                <RegimeWidget />
                <MarketBand livePrices={livePriceMap} />
            </FoldSection>
            </div>}

            {/* Main content: Leaderboard left, Live Prices right */}
            {/* Full width. Live Prices moved into the market section on the
                left, which is what freed the whole page for this table --
                Guillaume: "now the Top Traders Table would have the whole width
                of the page just for him." */}
            {/* Lite hides it but never locks it: "See all traders" (?all=1) still opens it. */}
            {(dashboardSectionInMode("dash-leaderboard", uiMode) || new URLSearchParams(window.location.search).get('all') === '1') && <div data-tour="dash-leaderboard">
            <FoldSection id="dash-leaderboard" title={t('section.leaderboard')} defaultOpen={uiMode === "pro"}
                forceOpen={new URLSearchParams(window.location.search).get('all') === '1'}>
            <div className="mb-8">
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-base font-semibold text-foreground">{t('top.title')}</h2>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {period === '1w'
                                    ? t('top.subtitle.week')
                                    : period === '1m'
                                        ? t('top.subtitle.month')
                                        : t('top.subtitle.all')}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Always mounted, so a shared ?vs= link opens it even
                                for someone whose own list is empty; the button
                                itself shows once there is someone to compare. */}
                            <VsLauncher size="sm" className={`h-8 gap-1.5 ${compareNames.length ? '' : 'hidden'}`}>
                                <Scales size={14} /> {t('top.vs', { n: compareNames.length })}
                            </VsLauncher>
                            {/* A window, not a filter: it changes which trades the
                                ranking is computed FROM, so a trader who was
                                excellent last year and idle this month falls. */}
                            <div data-tour="dash-period" className="flex items-center gap-1 p-1 rounded-lg bg-muted/30 border border-border/30">
                                {([['all', 'period.all'], ['1m', 'period.month'], ['1w', 'period.week']] as const).map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        // The welcome guide waits for the person's own choice (lib/onboarding.ts).
                                        onClick={() => { setPeriod(value); emitOnboarding('dash_period', { at: Date.now() }); }}
                                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                                            period === value
                                                ? 'bg-primary text-primary-foreground'
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        {t(label)}
                                    </button>
                                ))}
                            </div>
                            <Select value={sortBy} onValueChange={(v) => {
                                setSortBy(v);
                                // Pick the direction the column itself considers
                                // good, so choosing "Worst Trade" shows the worst
                                // rather than the flattest.
                                const picked = TRADER_COLUMNS.find(c => c.key === v);
                                setSortDesc(picked ? !picked.lowerBetter : true);
                                setTraderPage(0);
                                emitOnboarding('dash_sort', { at: Date.now() });
                            }}>
                                <SelectTrigger data-tour="dash-sort" className="w-[150px] h-8 text-xs" aria-label={t('mobile:leaderboard.sortTitle')}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {/* Generated from the column definitions, so a
                                        new column is sortable from here the moment
                                        it exists. This list was hand-written and
                                        eleven entries short. */}
                                    {TRADER_COLUMNS.map(col => (
                                        <SelectItem key={col.key} value={col.key}>{t(col.label)}</SelectItem>
                                    ))}
                                    <SelectItem value={BALANCED_SORT}>{t('top.balanced')}</SelectItem>
                                </SelectContent>
                            </Select>
                            {/* Columns picker — every trader-profile metric is available (shared, components/TraderColumnPicker). */}
                            <TraderColumnPicker visible={visibleCols} onChange={setVisibleCols} defaults={DEFAULT_TRADER_COLS} />

                            {tradersArePrevious && assetFilterActive(leaderAssets) && (
                                <span className="text-[11px] text-warning animate-pulse">{t('common:asset.filter.loading')}</span>
                            )}

                            {/* Filters — min/max on any metric.

                                data-tour is on the BUTTON, not the Popover:
                                the thing the Orb has to make glow is the
                                closed trigger someone cannot find, not the
                                panel behind it, which does not exist in the
                                DOM until it is opened. This is the control a
                                user was told to use while standing on a
                                different screen entirely (2026-09-23). */}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button data-tour="dash-filters" variant="outline" size="sm" className="h-8 text-xs gap-1.5">
                                        {t('filters.button')}
                                        {activeFilterCount > 0 && (
                                            <Badge variant="secondary" className="h-4 px-1 text-[10px]">{activeFilterCount}</Badge>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent data-tour="dash-filters-panel" align="end" className="w-80 max-h-[420px] overflow-y-auto">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs font-semibold">{t('filters.title')}</p>
                                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={clearFilters}>
                                            {t('filters.clearAll')}
                                        </Button>
                                    </div>
                                    <div className="flex items-center gap-2 p-2 mb-2 rounded-lg border border-border/60 bg-muted/30">
                                        <Switch
                                            id="favourites-only"
                                            checked={favouritesOnly}
                                            onCheckedChange={(v) => { setFavouritesOnly(v); setTraderPage(0); emitOnboarding('dash_filter', { at: Date.now() }); }}
                                        />
                                        <label htmlFor="favourites-only" className="cursor-pointer text-xs font-medium">
                                            {t('filters.favouritesOnly')}
                                        </label>
                                    </div>
                                    {/* The below-floor door. Guillaume asked for the
                                        traders the catastrophic-loss floor hides to be
                                        REACHABLE for Reverse-mode users, which is not
                                        the same as listed: off on every load, never
                                        remembered, and the rows it adds are badged. */}
                                    <div className="flex items-start gap-2 p-2 mb-2 rounded-lg border border-border/60 bg-muted/30">
                                        <Switch
                                            id="below-floor"
                                            checked={includeBelowFloor}
                                            onCheckedChange={(v) => { setIncludeBelowFloor(v); setTraderPage(0); emitOnboarding('dash_filter', { at: Date.now() }); }}
                                            className="mt-0.5"
                                        />
                                        {/* The name alone; the explanation on hover (review #20, 10-25). */}
                                        <label htmlFor="below-floor" className="cursor-pointer" title={t('filters.heavyLosers.desc')}>
                                            <span className="text-xs font-medium">{t('filters.heavyLosers')}</span>
                                        </label>
                                    </div>
                                    {/* Asset type and broker availability: the leaderboard
                                        ranks each trader on the matching trades only. */}
                                    <div className="p-2 mb-2 rounded-lg border border-border/60 bg-muted/30">
                                        <p className="text-xs font-medium mb-1.5">{t('common:asset.filter.title')}</p>
                                        <AssetFilterControls value={leaderAssets} onChange={(f) => { setLeaderAssets(f); setTraderPage(0); emitOnboarding('dash_filter', { at: Date.now() }); }} />
                                    </div>

                                    <TraderMetricFilters
                                        value={colFilters}
                                        onChange={(f) => { setColFilters(f); setTraderPage(0); emitOnboarding('dash_filter', { at: Date.now() }); }}
                                        otherActive={activeFilterCount - colFilterCount(colFilters)}
                                    />
                                </PopoverContent>
                            </Popover>

                            <Button variant="outline" size="sm" asChild>
                                <Link to="/traders" className="gap-1.5">
                                    {t('top.viewAll')} <CaretRight className="rtl:-scale-x-100" size={14} />
                                </Link>
                            </Button>
                        </div>
                    </div>

                    <div className="glass-card rounded-xl overflow-hidden">
                        <Table className="[&_th]:px-2 [&_td]:px-2 [&_td]:py-2.5">
                            <TableHeader>
                                <TableRow data-tour="dash-headers" className="hover:bg-transparent">
                                    <TableHead className="w-12">{t('top.rank')}</TableHead>
                                    <TableHead>{t('top.trader')}</TableHead>
                                    {shownColumns.map(col => (
                                        <TableHead
                                            key={col.key}
                                            className="w-px text-center select-none whitespace-nowrap"
                                        >
                                            <span className="inline-flex items-center gap-1 justify-center w-full">
                                                <span
                                                    className="cursor-pointer hover:text-foreground"
                                                    onClick={() => sortByColumn(col)}
                                                    title={t('top.sortBy', { label: t(col.label) })}
                                                >
                                                    {t(col.label)}{sortBy === col.key ? (sortDesc ? ' \u2193' : ' \u2191') : ''}
                                                </span>
                                                {(col.info || col.blurb) && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span className="text-muted-foreground cursor-help"><Info size={12} /></span>
                                                        </TooltipTrigger>
                                                        <TooltipContent className="max-w-[280px] text-xs font-normal normal-case">
                                                            {col.info ? t(col.info) : col.info}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </span>
                                        </TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody data-tour="dash-rows">
                                {paginatedTraders.map((trader: any, i: number) => {
                                    const globalRank = safeTraderPage * tradersPerPage + i + 1;
                                    return (
                                        <TableRow
                                            key={trader.trader_id || i}
                                            className="cursor-pointer hover:bg-white/[0.04]"
                                            onClick={() => {
                                                /* The profile opens on the table's period, so the row
                                                   and the profile show the same figures (2026-10-16). */
                                                if (trader.traders?.trader_name) navigate(traderPath(trader.traders.trader_name, trader.trader_id) + (period !== 'all' ? `?period=${period}` : ''), { state: traderLinkState(trader.trader_id) });
                                            }}
                                        >
                                            <TableCell className="text-muted-foreground text-sm">#{globalRank}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2.5">
                                                    <Avatar size={28} name={trader.traders?.trader_name || `t-${i}`} variant="beam" colors={['#2563eb', '#10b981', '#64748b', '#0ea5e9', '#8b5cf6']} />
                                                    {/* truncate + min-w-0 on the NAME, shrink-0 on the badge.
                                                        The row is a flex line and the badge was the flexible
                                                        item, so a long pseudonym squeezed it until "Heavy
                                                        loss" wrapped onto two lines inside a box given the
                                                        height of one -- text spilling out of its own border.
                                                        The name is the part that should give way. */}
                                                    <span className="text-sm font-medium truncate min-w-0">{trader.traders?.trader_name || t('top.unknown')}</span>
                                                    {trader.traders?.trader_name && (() => {
                                                        const name = trader.traders.trader_name;
                                                        const inIt = compareNames.includes(name);
                                                        return (
                                                            <button
                                                                type="button"
                                                                aria-label={inIt ? t('top.compareRemove', { name }) : t('top.compareAdd', { name })}
                                                                title={inIt ? t('top.inComparison') : t('top.addToVs')}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (inIt) { removeFromCompare(name); return; }
                                                                    if (!addToCompare(name)) toast.error(t('top.compareMax', { max: COMPARE_MAX }));
                                                                }}
                                                                className={`shrink-0 rounded p-0.5 transition-colors ${inIt ? 'text-primary' : 'text-muted-foreground/50 hover:text-foreground'}`}
                                                            >
                                                                <Scales size={14} weight={inIt ? 'fill' : 'regular'} />
                                                            </button>
                                                        );
                                                    })()}
                                                    {trader.below_floor && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Badge variant="outline" className="shrink-0 h-4 px-1.5 text-[10px] leading-none whitespace-nowrap border-danger/40 text-danger">
                                                                    {t('top.heavyLoss')}
                                                                </Badge>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-[260px] text-xs">
                                                                {t('top.heavyLoss.info')}
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                    {/* Farm account (Guillaume, 2026-10-20): tagged, never hidden. */}
                                                    {SHOW_FARM_TAG && trader.is_farm && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Badge variant="outline" className="shrink-0 h-4 px-1.5 text-[10px] leading-none whitespace-nowrap border-warning/40 text-warning">
                                                                    {t('top.farm')}
                                                                </Badge>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-[260px] text-xs">
                                                                {t('top.farm.info')}
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                    {trader.little_history && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Badge variant="outline" className="shrink-0 h-4 px-1.5 text-[10px] leading-none whitespace-nowrap border-warning/40 text-warning">
                                                                    {t('top.littleHistory')}
                                                                </Badge>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-[260px] text-xs">
                                                                {t('top.littleHistory.info')}
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </TableCell>
                                            {shownColumns.map(col => (
                                                <TableCell key={col.key} className="w-px text-center text-sm whitespace-nowrap">
                                                    {/* Closed nothing in this period: "-", not a row of zeros
                                                        that read as a flat result (2026-10-16). Open positions
                                                        and the wallet are now, not a period. */}
                                                    {trader.no_trades_in_period && !NOW_COLUMNS.has(col.key)
                                                        ? <span className="text-muted-foreground" title={t('top.noTradesInPeriod')}>—</span>
                                                        : col.render(trader)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                                {/* isError BEFORE the empty branch. "No trader data
                                    available yet" is a claim about the world; a failed
                                    request is a claim about us, and telling the second
                                    story with the first sentence is the same lie as
                                    telling a user their own rules filtered an order
                                    when our code broke. */}
                                {tradersError && (
                                    <TableRow>
                                        <TableCell colSpan={2 + shownColumns.length} className="text-center py-8">
                                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                                <WarningCircle size={22} className="text-warning" />
                                                <p className="text-sm">{t('top.loadFailed')}</p>
                                                <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5"
                                                    onClick={() => refetchTraders()}>
                                                    <ArrowClockwise size={13} /> {t('tryAgain')}
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!tradersError && (!topTraders || topTraders.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={2 + shownColumns.length} className="text-center py-8 text-muted-foreground">
                                            {t('top.noData')}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!tradersError && (topTraders?.length || 0) > 0 && sortedTraders.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={2 + shownColumns.length} className="text-center py-8 text-muted-foreground">
                                            <p className="text-sm">{t('top.noMatch')}</p>
                                            <Button variant="ghost" size="sm" className="h-7 text-xs mt-1" onClick={clearFilters}>
                                                {t('top.clearFilters')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {totalTraderPages > 1 && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
                                <span className="text-xs text-muted-foreground">{t('top.page', { page: safeTraderPage + 1, total: totalTraderPages })}</span>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setTraderPage(Math.max(0, safeTraderPage - 1))} disabled={safeTraderPage === 0}>
                                        <CaretLeft className="rtl:-scale-x-100" size={14} /> {t('top.previous')}
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => setTraderPage(Math.min(totalTraderPages - 1, safeTraderPage + 1))} disabled={safeTraderPage >= totalTraderPages - 1}>
                                        {t('top.next')} <CaretRight className="rtl:-scale-x-100" size={14} />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            </FoldSection>
            </div>}

            {/* Trades Section */}
            {dashboardSectionInMode("dash-trades", uiMode) && <FoldSection id="dash-trades" title={t('section.trades')} defaultOpen={uiMode === "pro"}>
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex flex-wrap items-center gap-4">
                        <button
                            onClick={() => setTradeView('recent')}
                            className={`text-base font-semibold transition-colors ${tradeView === 'recent' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            {t('trades.recent')}
                        </button>
                        <button
                            onClick={() => setTradeView('best')}
                            className={`text-base font-semibold transition-colors ${tradeView === 'best' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            {t('trades.best')}
                        </button>
                        <button
                            onClick={() => setTradeView('opened')}
                            className={`text-base font-semibold transition-colors ${tradeView === 'opened' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            {t('trades.opened')}
                        </button>
                        <AssetFilterControls value={tradeAssets} onChange={setTradeAssets} />
                        {assetFilterActive(tradeAssets) && (tradeView === 'recent' ? recentArePrevious
                            : tradeView === 'opened' ? openedArePrevious : topTradesArePreviousWindow) && (
                            <span className="text-[11px] text-warning animate-pulse">{t('common:asset.filter.loading')}</span>
                        )}
                        {/* Only on Best Trades, and served by the endpoint
                            that actually ranks over a window. "Best this week"
                            has to be computed across the week's trades, not
                            filtered out of the hundred most recent -- which is
                            why the previous version could not work. */}
                        {tradeView === 'best' && (
                            <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted/30 border border-border/30">
                                {([['24h', 'trades.window.24h'], ['1w', 'trades.window.1w'], ['1m', 'trades.window.1m']] as const).map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setTradePeriod(value)}
                                        className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
                                            tradePeriod === value
                                                ? 'bg-primary text-primary-foreground'
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        {t(label)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <LiveIndicator connected={wsConnected} />
                </div>

                <div className="glass-card rounded-xl overflow-hidden">
                    <Table className="[&_th]:px-2 [&_td]:px-2 [&_td]:py-2.5">
                        <TableHeader>
                            <TableRow className="hover:bg-transparent">
                                {/* Columns expanded 2026-05-16 per Nick: surface Margin, Duration, Orders, Fees, Drawdown, Closing Price. Responsive hiding keeps mobile sane. */}
                                {/* No w-px on these columns (Guillaume, 2026-09-26: "a lot of absurd unused space"): with every column but Symbol and Trader pinned narrow, all the spare width landed in those two. Unpinned, it spreads across the row. */}
                                <TableHead className="text-center">{tradeView === 'opened' ? t('trades.col.opened') : t('trades.col.closed')}</TableHead>
                                <TableHead>{t('trades.col.symbol')}</TableHead>
                                <TableHead>{t('trades.col.trader')}</TableHead>
                                <TableHead className="text-center">{t('trades.col.side')}</TableHead>
                                <TableHead className="text-center hidden md:table-cell">{t('trades.col.size')}</TableHead>
                                <TableHead className="text-center hidden md:table-cell">{t('trades.col.lev')}</TableHead>
                                <TableHead className="text-center hidden md:table-cell">{t('trades.col.margin')}</TableHead>
                                <TableHead className="text-center hidden xl:table-cell">{tradeView === 'opened' ? t('trades.col.mark') : t('trades.col.close')}</TableHead>
                                <TableHead className="text-center hidden lg:table-cell">{t('trades.col.duration')}</TableHead>
                                <TableHead className="text-center hidden lg:table-cell">{t('trades.col.orders')}</TableHead>
                                <TableHead className="text-center hidden xl:table-cell">{t('trades.col.fees')}</TableHead>
                                <TableHead className="text-center hidden xl:table-cell">{t('trades.col.dd')}</TableHead>
                                <TableHead className="text-center">{t('trades.col.pnl')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(() => {
                                /* Two views, two sources.
                                   Recent = the latest trades, as they came.
                                   Best   = ranked BY THE SERVER over the chosen
                                            window. Filtering the recent hundred
                                            could never do this: all hundred
                                            closed within a day, so a week or a
                                            month removed nothing and the best
                                            of the month simply was not in the
                                            list to find. */
                                /* Recently Opened: still open, so the time is the
                                   open, the price is the live mark, the duration
                                   runs, and the PnL is priced live (lib/openPnl). */
                                const nowMs = Date.now();
                                const opened = (openedTrades || []).map((t: any) => {
                                    // Live tick first, the venue's own mark second (M-84's rule).
                                    const mark = livePriceMap[t.symbol] || Number(t.mark_price) || 0;
                                    const pnl = openPnlOf({
                                        markPrice: mark,
                                        entry: t.avg_entry_price || 0,
                                        size: t.initial_size || 0,
                                        isLong: String(t.direction || '').toUpperCase().startsWith('LONG'),
                                        fallback: 0,
                                    });
                                    return {
                                        ...t,
                                        exit_time: t.entry_time,
                                        close_price: mark,
                                        duration_hours: t.entry_time ? (nowMs - new Date(t.entry_time).getTime()) / 3_600_000 : 0,
                                        pnl_usd: pnl,
                                        pnl_percent: t.margin_dollar > 0 ? (pnl / t.margin_dollar) * 100 : 0,
                                    };
                                });
                                const rows = tradeView === 'best'
                                    ? (topTrades || [])
                                    : tradeView === 'opened'
                                        ? opened
                                        : (recentTrades || []);
                                /* Best Trades only: Recent Trades has one
                                   window and cannot show the wrong one. */
                                if (tradeView === 'best' && bestTradesState !== 'rows' && tradePage === 0) {
                                    return [];
                                }
                                // The pages already read, then this one once it has
                                // arrived (until then the hook holds the page before).
                                const pending = tradeView === 'best' ? topTradesArePreviousWindow
                                    : tradeView === 'opened' ? openedArePrevious : recentArePrevious;
                                const page = tradePage > 0 && pending ? [] : rows;
                                // A trade that moved down a page as new ones arrived is listed once.
                                const seenIds = new Set(earlierTradeRows.map((r: any) => r.id));
                                const shown = [...earlierTradeRows, ...page.filter((r: any) => !seenIds.has(r.id))];
                                tradesShown.current = shown;
                                if (shown.length > 0) tradesRowsHeld.current = shown.length;
                                return shown;
                            })().map((trade: any, i: number, rowsInThisTable: any[]) => {
                                const fmtPrice = (p: number) => {
                                    if (!p) return '—';
                                    if (p >= 1000) return fmtUsd(p, 2, { minDigits: 0 });
                                    if (p >= 1) return fmtUsd(p, 4, { group: false });
                                    // toPrecision(4) in the user's language; below 1e-6 toPrecision writes
                                    // an exponent, which Intl does not, so that case keeps the old text.
                                    if (Math.abs(p) < 1e-6) return `$${p.toPrecision(4)}`;
                                    return new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', minimumSignificantDigits: 4, maximumSignificantDigits: 4, useGrouping: false }).format(p);
                                };
                                const fmtDuration = (h: number) => {
                                    if (!h || h <= 0) return '—';
                                    if (h < 1) return `${Math.round(h * 60)}m`;
                                    if (h < 24) return `${fmtFixed(h, 1)}h`;
                                    return `${fmtFixed(h / 24, 1)}d`;
                                };
                                const fmtCompactUsd = (v: number) => {
                                    if (!v && v !== 0) return '—';
                                    if (Math.abs(v) >= 1000) return `$${fmtFixed(v / 1000, 1)}k`;
                                    return fmtUsd(v, 2, { group: false });
                                };
                                const fmtSize = (s: number) => {
                                    if (s == null || !isFinite(s) || s === 0) return '—';
                                    if (Math.abs(s) >= 1) return fmtFixed(s, 4);
                                    // toPrecision(5) in the user's language (exponent range kept as it was).
                                    if (Math.abs(s) < 1e-6) return s.toPrecision(5);
                                    return s.toLocaleString(intlLocale(), { minimumSignificantDigits: 5, maximumSignificantDigits: 5, useGrouping: false });
                                };
                                const incr = trade.increase_count || 0;
                                const decr = trade.decrease_count || 0;
                                return (
                                <TableRow
                                    key={i}
                                    className="hover:bg-white/[0.04] cursor-pointer"
                                    onClick={() => { setNavTrades(rowsInThisTable); setSelectedTrade({
                                        ...trade,
                                        id: trade.id,
                                        tradeId: trade.id || `TRD-${i}`,
                                        asset: trade.symbol || 'BTC',
                                        direction: trade.direction,
                                        entry: fmtPrice(trade.avg_entry_price || 0),
                                        exit: fmtPrice(trade.avg_exit_price || 0),
                                        pnl: `${withSign(trade.pnl_usd >= 0, fmtUsd(trade.pnl_usd, 3, { minDigits: 0 }))}`,
                                        entryTime: trade.entry_time,
                                        exitTime: trade.exit_time,
                                    })}}
                                >
                                    <TableCell className="text-center text-muted-foreground text-xs whitespace-nowrap">
                                        <DateTimeCell value={trade.exit_time} />
                                    </TableCell>
                                    <TableCell className="text-sm font-medium"><SymbolLabel symbol={trade.symbol} /></TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Avatar size={20} name={trade.traders?.trader_name || `t-${i}`} variant="beam" colors={['#2563eb', '#10b981', '#64748b', '#0ea5e9', '#8b5cf6']} />
                                            <span className="text-sm text-muted-foreground">{trade.traders?.trader_name || '—'}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <Badge
                                            variant={(trade.direction || '').toUpperCase().startsWith('LONG') ? 'default' : 'destructive'}
                                            className={`text-[10px] ${(trade.direction || '').toUpperCase().startsWith('LONG') ? 'bg-success/20 text-success border-success/30' : 'bg-danger/20 text-danger border-danger/30'}`}
                                        >
                                            {trade.direction}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-center text-xs font-mono hidden md:table-cell">
                                        {fmtSize(trade.initial_size)}
                                    </TableCell>
                                    <TableCell className="text-center text-xs font-mono text-muted-foreground hidden md:table-cell">
                                        {trade.leverage ? `${trade.leverage}×` : '—'}
                                    </TableCell>
                                    <TableCell className="text-center text-xs font-mono hidden md:table-cell">
                                        {fmtCompactUsd(trade.margin_dollar)}
                                    </TableCell>
                                    <TableCell className="text-center text-xs font-mono text-muted-foreground hidden xl:table-cell">
                                        {fmtPrice(trade.close_price)}
                                    </TableCell>
                                    <TableCell className="text-center text-xs text-muted-foreground hidden lg:table-cell">
                                        {fmtDuration(trade.duration_hours)}
                                    </TableCell>
                                    <TableCell className="text-center text-xs text-muted-foreground hidden lg:table-cell whitespace-nowrap" title={t('trades.ordersTitle', { incr, decr, total: trade.order_count || 0 })}>
                                        {trade.order_count || 0}
                                        {(incr > 0 || decr > 0) && (
                                            <span className="ms-1 opacity-50">({incr}↑{decr}↓)</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center text-xs font-mono text-muted-foreground hidden xl:table-cell">
                                        {fmtCompactUsd(trade.total_fees)}
                                    </TableCell>
                                    <TableCell className="text-center text-xs font-mono text-muted-foreground hidden xl:table-cell">
                                        {trade.drawdown_pct != null ? fmtPct(trade.drawdown_pct, 1) : '—'}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <div className={`flex flex-col items-end ${(trade.pnl_usd || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                            <span className="text-sm font-medium">{withSign((trade.pnl_usd || 0) >= 0, fmtUsd(trade.pnl_usd || 0, 3, { minDigits: 0 }))}</span>
                                            <span className="text-[10px] opacity-70">{withSign((trade.pnl_percent || 0) >= 0, fmtPct(trade.pnl_percent || 0, 2))}</span>
                                            {/* WHAT IS LEFT AFTER COSTS, when costs change the answer.
                                                The Fees column two cells over states the charge and
                                                leaves the subtraction to the reader, so a trade that
                                                made $0.40 and paid $0.55 still reads green. Mobile has
                                                shown the net beside the gross since August
                                                (TradeCard) and applies exactly this rule: the footnote
                                                appears only when the two differ, because a second
                                                figure identical to the first is noise. Funding keeps
                                                its sign -- it is a credit when the position was paid. */}
                                            {(() => {
                                                const gross = trade.pnl_usd || 0;
                                                const net = gross - (trade.total_fees || 0) - ((trade as any).funding_paid || 0);
                                                return Math.abs(gross - net) >= 0.005 ? (
                                                    <span className="text-[10px] text-muted-foreground">
                                                        {t('top.netAfterCosts', { value: withSign(net >= 0, fmtUsd(net, 3, { minDigits: 0 })) })}
                                                    </span>
                                                ) : null;
                                            })()}
                                        </div>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                            {tradeView === 'recent' && recentTradesError && (
                                <TableRow>
                                    <TableCell colSpan={13} className="text-center py-8">
                                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                            <WarningCircle size={22} className="text-warning" />
                                            <p className="text-sm">{t('trades.loadFailed')}</p>
                                            <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5"
                                                onClick={() => refetchRecentTrades()}>
                                                <ArrowClockwise size={13} /> {t('tryAgain')}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                            {tradeView === 'recent' && !recentTradesError && recentTrades && recentTrades.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                                        {t('trades.noRecent')}
                                    </TableCell>
                                </TableRow>
                            )}
                            {tradeView === 'opened' && openedTrades && openedTrades.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                                        {t('trades.noOpened')}
                                    </TableCell>
                                </TableRow>
                            )}
                            {/* A view that has not arrived yet says so, at the height the
                                table had a moment ago (GB-3, 2026-09-25). Before, Recent
                                said "No recent trades available" while it was still loading,
                                and Recently Opened drew nothing at all. */}
                            {((tradeView === 'recent' && !recentTradesError && !recentTrades) ||
                              (tradeView === 'opened' && !openedTrades)) && (
                                <>
                                    <TableRow>
                                        <TableCell colSpan={13} className="text-center py-8 text-muted-foreground text-sm">
                                            {tradeView === 'opened' ? t('trades.loadingOpened') : t('trades.loadingRecent')}
                                        </TableCell>
                                    </TableRow>
                                    {Array.from({ length: tradesRowsHeld.current }).map((_, i) => (
                                        <TableRow key={`hold-${i}`} aria-hidden className="invisible">
                                            <TableCell colSpan={13} className="py-3">&nbsp;</TableCell>
                                        </TableRow>
                                    ))}
                                </>
                            )}
                            {/* GB-3: one row that says which of the three
                                things is true, instead of the previous
                                window's rows under the new label. */}
                            {tradeView === 'best' && (() => {
                                // lib/tradeTableState's tradeTableMessage, with the sentences translated.
                                const msg = bestTradesState === 'switching'
                                    ? t(`trades.bestLoading.${bestTradesWindowLabel}`)
                                    : bestTradesState === 'failed'
                                        ? t('trades.bestFailed')
                                        : bestTradesState === 'empty'
                                            ? t(`trades.bestEmpty.${bestTradesWindowLabel}`)
                                            : null;
                                if (!msg) return null;
                                return (
                                    <>
                                        <TableRow>
                                            <TableCell colSpan={13} className="text-center py-8 text-muted-foreground text-sm">
                                                {msg}
                                            </TableCell>
                                        </TableRow>
                                        {/* The height the table had a moment ago, held so the
                                            page does not collapse and take the scroll position
                                            with it. See GB-3 above. Empty and aria-hidden: it
                                            reserves space and says nothing. */}
                                        {Array.from({ length: tradesRowsHeld.current }).map((_, i) => (
                                            <TableRow key={`hold-${i}`} aria-hidden className="invisible">
                                                <TableCell colSpan={13} className="py-3">&nbsp;</TableCell>
                                            </TableRow>
                                        ))}
                                    </>
                                );
                            })()}
                        </TableBody>
                    </Table>
                    {tradePage + 1 < totalTradePages && (
                        <div className="flex justify-center px-4 py-3 border-t border-white/[0.06]">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={tradesShown.current.length < (tradePage + 1) * TRADES_PER_PAGE && tradePage > 0}
                                onClick={() => {
                                    setEarlierTradeRows(tradesShown.current);
                                    setTradePage(tradePage + 1);
                                }}
                            >
                                {t('common:assets.showMore', { count: Math.min(TRADES_PER_PAGE, Math.max(0, tradesTotal - tradesShown.current.length)) })}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            </FoldSection>}

            {/* Trade Details Modal */}
            <React.Suspense fallback={null}>
                <TradeDetailsModal
                    trade={selectedTrade}
                    isOpen={!!selectedTrade}
                    onClose={() => setSelectedTrade(null)}
                    onPrevious={handlePrevTrade}
                    onNext={handleNextTrade}
                    showViewTraderProfileButton={true}
                    onViewTraderProfile={selectedTrade ? () => {
                        const traderId = selectedTrade.trader_id || selectedTrade.trader_uid;
                        if (!traderId) return;
                        // Close first, then navigate. A Radix dialog torn down by
                        // a route change instead of by its own close can leave
                        // pointer-events:none on the body -- the destination
                        // renders and then ignores every click, which looks
                        // exactly like a page that failed to load.
                        setSelectedTrade(null);
                        navigate(traderPath(selectedTrade.traders?.trader_name || selectedTrade.trader_name, traderId), { state: traderLinkState(traderId) });
                    } : undefined}
                />
            </React.Suspense>

            {/* No price-chart pop-up here. A price row opens the asset's own page
                (LivePricesTable, 2026-10-06), so the dialog had no way in and
                was removed. Guillaume, 2026-10-08 (M-117): "Remove the pop-up". */}
        </div>
    );
}
